import 'match-media';
import {isMobile, isTablet} from 'Utils/deviceUtils';
import {getDocumentScrollTop} from 'Utils/scrollUtils';

const setDomStyle = domList => {
    domList.forEach(domElement => {
        // Check the content of the cell
        if(domElement.firstElementChild.offsetHeight === 0 && (domElement.lastElementChild.offsetHeight + 20) < domElement.offsetHeight) {
            // And put an offset to make it appears vertically centered
            domElement.lastElementChild.style.marginTop = `${((domElement.offsetHeight - (domElement.lastElementChild.offsetHeight + 20)) / 2) / 10}rem`;
        } else {
            domElement.lastElementChild.style.marginTop = '0';
        }
    });
};

const getMinLimitedRowHeightInPx = domRow => {
    let minLimitedRowHeight = parseInt(domRow.querySelector('.title').offsetHeight, 10) + 20;

    if(domRow.querySelector('.subtitle') !== null) {
        minLimitedRowHeight += parseInt(domRow.querySelector('.subtitle').offsetHeight, 10) + 5;
    }

    if(domRow.querySelector('.link') !== null) {
        minLimitedRowHeight += parseInt(domRow.querySelector('.link').offsetHeight, 10) + 5;
    }

    return minLimitedRowHeight;
};

const getRowOffsetHeightInPx = domRow => {
    let rowOffsetHeight = domRow.offsetHeight;

    if(domRow.classList.contains('limited')) {
        const minLimitedRowHeight = getMinLimitedRowHeightInPx(domRow);

        if(rowOffsetHeight < minLimitedRowHeight) {
            rowOffsetHeight = minLimitedRowHeight;
        }
    }

    return rowOffsetHeight;
};

export default class GuaranteeTableController {
    // eslint-disable-next-line max-statements
    constructor(domElement) {
        if(!domElement) {
            throw new DOMException('element is undefined');
        }

        const isInitialized = (domElement.getAttribute('data-guarantee-table-initialized') || 'false') === 'true';

        if(isInitialized === true) {
            return;
        }

        domElement.guaranteeTableCtl = this;
        domElement.dataset.guaranteeTableInitialized = 'true';

        this.domElement = domElement;
        this._bannerPageElement = document.querySelector('.page-banner');

        this.initialize();
        this.updateHeaderLayout();
        this.updateRowLayout();
        this.updateScrollLayout();
        this.handleTableScroll();

        this.domElement.classList.add('displayable');

        this.registerEvents();
    }

    // eslint-disable-next-line max-statements
    initialize() {
        this._captionRows = [...this.domElement.querySelectorAll('.matrix-caption .row')];
        this._table = this.domElement.querySelector('.matrix-table');
        this._packLimitedRows = [...this.domElement.querySelectorAll('.row.limited')];
        this._tableHeader = this._table.querySelector('.row.guarantee-table__header');
        this._tableRows = [...this._table.querySelectorAll('.row:not(.guarantee-table__header)')];
        this._links = [...this.domElement.querySelectorAll('.link')];
        this._stickyHeader = this.domElement.querySelector('.matrix-sticky');
        this._visibleColumnIndex = 0;
        this._progress = this.domElement.querySelector('.row.progress');
        this._columnCount = this._tableRows[0].querySelectorAll('.cell').length;
        this._scrollPrev = this.domElement.querySelector('.matrix-scroll.left');
        this._scrollNext = this.domElement.querySelector('.matrix-scroll.right');
        this._packListItems = [...this.domElement.querySelectorAll('.pack-list .item')];
        this._topPackSelect = this.domElement.querySelector('.matrix-container > .pack-select');
        this._stickyPackSelect = this.domElement.querySelector('.matrix-sticky .pack-select');
    }

    updateHeaderLayout() {
        setDomStyle([...this._tableHeader.querySelectorAll('.cell')]);
        setDomStyle([...this._stickyHeader.querySelectorAll('.cell')]);
    }

    updateRowLayout() {
        this._captionRows.forEach((domRow, rowIndex) => {
            const rowOffsetHeight = getRowOffsetHeightInPx(domRow);

            if(!domRow.dataset.rowHeight || domRow.classList.contains('limited')) {
                domRow.dataset.rowHeight = `${rowOffsetHeight / 10}rem`;
            }

            domRow.style.height = `${rowOffsetHeight}px`;

            if(this._tableRows.length > rowIndex) {
                this._tableRows[rowIndex].style.height = `${rowOffsetHeight / 10}rem`;
                this.verticalAlignCellContent(rowIndex, rowOffsetHeight);
            }
        }, this);

        [...this.domElement.querySelectorAll('.row.caption')].forEach(domCaption => {
            if(domCaption.nextElementSibling.offsetHeight === 0) {
                domCaption.classList.add('hidden');
            }
        }, this);
    }

    verticalAlignCellContent(rowIndex, offsetHeight) {
        if(isMobile()) {
            [...this._tableRows[rowIndex].querySelectorAll('.cell-content')].forEach(domCellContent => {
                domCellContent.style.top = `${(offsetHeight - 20 - domCellContent.offsetHeight) / 2}px`;
            });
        } else {
            [...this._tableRows[rowIndex].querySelectorAll('.cell-content')].forEach(domCellContent => {
                const alignedPixels = (offsetHeight - 10 - domCellContent.offsetHeight) / 2;

                domCellContent.style.top = `${Math.floor((alignedPixels * 100) / offsetHeight)}%`;
            });
        }
    }

    updateScrollLayout() {
        if(this._timeout) {
            window.clearTimeout(this._timeout);
        }

        this._timeout = window.setTimeout(() => {
            const scrollTop = getDocumentScrollTop();
            const {offsetTop, offsetHeight} = this.domElement;
            const bannerHeight = this._bannerPageElement !== null ? this._bannerPageElement.offsetHeight : 0;

            [...this.domElement.querySelectorAll('.matrix-scroll')].forEach(button => {
                button.style.top = `${((scrollTop - offsetTop - bannerHeight) / 2 + (offsetHeight / 2)) / 10}rem`;
            });
        }, 50);

        // Show/hide sticky header
        let offset = this.domElement.offsetTop;

        if(isMobile()) {
            offset += 465;
        } else if(this._bannerPageElement !== null) {
            offset += this._bannerPageElement.offsetHeight;
        }

        if(getDocumentScrollTop() >= offset) {
            this.domElement.classList.add('sticky');
        } else {
            this.domElement.classList.remove('sticky');
        }
    }

    handleRowClick(evt) {
        if(isMobile()) {
            const row = evt.currentTarget;
            this.showCaptionDescription(row);
        }
    }

    handleLinkClick(evt) {
        if(isMobile()) {
            return;
        }
        const clickedElement = evt.target || evt.srcElement;
        let row = clickedElement.parentNode;
        while(!row.classList.contains('row')) {
            row = row.parentNode;
        }
        this.showCaptionDescription(row);
    }

    proceedNextVisibleElement(rowIndex, link) {
        const captionRowsHeight = this._captionRows[rowIndex].dataset.rowHeight;
        this._captionRows[rowIndex].style.height = captionRowsHeight;
        this._tableRows[rowIndex].style.height = captionRowsHeight;
        this.editElementAttributeValue(link, 'data-analytics-label', 'cacher', 'voir');
    }

    proceedNextNotVisibleElement(rowIndex, link, cell, style) {
        let heightAfter = 0;
        [...cell.childNodes].filter(domItem => domItem.offsetHeight).forEach(domItem => {
            heightAfter += domItem.offsetHeight;
        });

        const padding = isMobile() ? -8 : 0;
        const calculatedHeight = `${(heightAfter + parseInt(style.paddingTop, 10) + parseInt(style.paddingBottom, 10) + padding) / 10}rem`;
        this._captionRows[rowIndex].style.height = calculatedHeight;
        this._tableRows[rowIndex].style.height = calculatedHeight;
        this.editElementAttributeValue(link, 'data-analytics-label', 'voir', 'cacher');
    }

    // eslint-disable-next-line max-statements
    showCaptionDescription(row) {
        const rowIndex = this._captionRows.indexOf(row),
            link = row.querySelector('.link');

        if(link.nextElementSibling !== null) {
            const cell = link.parentNode,
                style = window.getComputedStyle(cell, null),
                isNextElementVisible = link.nextElementSibling.classList.contains('visible');

            if(isNextElementVisible) {
                this.proceedNextVisibleElement(rowIndex, link);
            } else {
                this.proceedNextNotVisibleElement(rowIndex, link, cell, style);
            }

            // Prevent the checks from being hidden by the open description on mobile
            if(!isMobile()) {
                this.verticalAlignCellContent(rowIndex, parseInt(this._tableRows[rowIndex].style.height, 10));
            } else {
                this._tableRows[rowIndex].classList.toggle('clicked');
            }

            row.classList.toggle('clicked');
            link.nextElementSibling.classList.toggle('visible');
        }
    }

    // eslint-disable-next-line class-methods-use-this
    editElementAttributeValue(domElement, attribute, test, replacement) {
        if(!domElement.getAttribute(attribute)) {
            return;
        }

        const newValue = domElement.getAttribute(attribute).replace(test, replacement);
        domElement.setAttribute(attribute, newValue);
    }

    toggleScrollVisibility() {
        if(this._visibleColumnIndex > 0) {
            this._scrollPrev.classList.add('visible');
        } else {
            this._scrollPrev.classList.remove('visible');
        }

        if(this._visibleColumnIndex - 2 >= 0) {
            this._scrollNext.classList.remove('visible');
        } else {
            this._scrollNext.classList.add('visible');
        }
    }

    applyScroll() {
        let columnOffset = 0;
        [...this._tableRows[0].querySelectorAll('.cell')].forEach((cell, cellIndex) => {
            if(cellIndex < this._visibleColumnIndex) {
                columnOffset += cell.offsetWidth + 10;
            }
        }, this);

        [...this._table.querySelectorAll('.row')].forEach(domRow => {
            domRow.style.transform = `translateX(-${columnOffset / 10}rem)`;
        }, this);

        [...this._stickyHeader.querySelectorAll('.cell')].forEach(domCell => {
            domCell.style.transform = `translateX(-${columnOffset / 10}rem)`;
        }, this);
    }

    handleTableScroll() {
        if(!(isMobile() || isTablet())) {
            return;
        }

        const currentScroll = this._table.scrollLeft;

        [...this._stickyHeader.querySelectorAll('.cell')].forEach(domCell => {
            domCell.style.transform = `translateX(-${currentScroll / 10}rem)`;
        }, this);

        let columnOffset = 0;
        let currentColumnIndex = 0;
        [...this._tableRows[0].querySelectorAll('.cell')].forEach((domCell, cellIndex) => {
            if(columnOffset <= (currentScroll + 90)) {
                currentColumnIndex = Math.max(cellIndex, currentColumnIndex);
            }

            columnOffset += domCell.offsetWidth;
        }, this);

        this._visibleColumnIndex = currentColumnIndex;

        [...this._progress.querySelectorAll('.bullet')].forEach((domBullet, bulletIndex) => {
            if(bulletIndex === currentColumnIndex) {
                domBullet.classList.add('current');
            } else {
                domBullet.classList.remove('current');
            }
        });
    }

    isStickyActive() {
        return this.domElement.classList.contains('sticky');
    }

    changePackSelectValue(domListItem, domRelatedPackSelect) {
        const listItemPackType = domListItem.dataset.packType,
            domCurrentTitle = domRelatedPackSelect.querySelector('.title .toggle-label span'),
            currentTitlePackType = domCurrentTitle.dataset.packType;

        // Update title properties with the clicked list item ones
        domCurrentTitle.innerHTML = domListItem.innerHTML;
        domCurrentTitle.dataset.packType = listItemPackType;

        // Hide the clicked list item entry in pack list
        domListItem.classList.add('hidden');

        // Show (former) title list item entry in pack list
        [...domRelatedPackSelect.querySelectorAll('.item')].forEach(domItem => {
            if(domItem.getAttribute('data-pack-type') === currentTitlePackType) {
                domItem.classList.remove('hidden');
            }
        }, this);

        // Hide pack list
        this.hidePackSelect(domRelatedPackSelect);
    }

    // eslint-disable-next-line class-methods-use-this
    hidePackSelect(domRelatedPackSelect) {
        domRelatedPackSelect.querySelector('.toggle').checked = false;
    }

    updateOtherPackSelect(newPackType) {
        const destPackSelect = this.isStickyActive() === true ? this._topPackSelect : this._stickyPackSelect,
            listItem = destPackSelect.querySelector(`[data-pack-type="${newPackType}"]`);

        this.changePackSelectValue(listItem, destPackSelect);
    }

    setTablePackType(newPackType) {
        this.domElement.dataset.packType = newPackType;
    }

    showPackLimitedRows(newPackType) {
        this._packLimitedRows.forEach(packLimitedRow => {
            if(packLimitedRow.classList.contains(newPackType)) {
                packLimitedRow.classList.remove('hidden');
            } else if(!packLimitedRow.classList.contains('hidden')) {
                packLimitedRow.classList.add('hidden');
            }
        });

        this.updateRowLayout();
    }

    hidePackSelectOnOutsideClick(evt) {
        const eventTarget = evt.srcElement || evt.target;

        // Ignore the input behavior linked to the pack list toggle label
        if(eventTarget.classList.contains('toggle')) {
            return;
        }

        if(!eventTarget.classList.contains('toggle-label')) {
            this.hidePackSelect(this._topPackSelect);
            this.hidePackSelect(this._stickyPackSelect);
        }
    }

    handleScrollPreviousClick() {
        this._visibleColumnIndex--;

        this._visibleColumnIndex = Math.max(0, this._visibleColumnIndex);

        this.applyScroll();
        this.toggleScrollVisibility();
    }

    handleScrollNextClick() {
        this._visibleColumnIndex++;

        this._visibleColumnIndex = Math.min(this._columnCount, this._visibleColumnIndex);

        this.applyScroll();
        this.toggleScrollVisibility();
    }

    handlePackListItemsClick(event) {
        const listItem = event.currentTarget,
            newPackType = listItem.getAttribute('data-pack-type'),
            relatedPackSelect = this.isStickyActive() ? this._stickyPackSelect : this._topPackSelect;

        this.changePackSelectValue(listItem, relatedPackSelect);
        this.updateOtherPackSelect(newPackType);
        this.setTablePackType(newPackType);
        this.showPackLimitedRows(newPackType);
    }

    handleTableGuaranteeClick(evt) {
        evt.stopPropagation();

        this.hidePackSelectOnOutsideClick(evt);
    }

    registerEvents() {
        window.addEventListener('resize', this.updateHeaderLayout.bind(this));
        window.addEventListener('scroll', this.updateScrollLayout.bind(this));

        document.querySelector('.table-guarantee').addEventListener('click', this.handleTableGuaranteeClick.bind(this));

        this._captionRows.forEach(domRow => {
            domRow.addEventListener('click', this.handleRowClick.bind(this));
        });

        this._links.forEach(domLink => {
            domLink.addEventListener('click', this.handleLinkClick.bind(this));
        });

        this._table.addEventListener('scroll', this.handleTableScroll.bind(this));

        this._scrollPrev.addEventListener('click', this.handleScrollPreviousClick.bind(this));

        this._scrollNext.addEventListener('click', this.handleScrollNextClick.bind(this));

        this._packListItems.forEach(domListItem => {
            domListItem.addEventListener('click', this.handlePackListItemsClick.bind(this));
        });
    }
}
